import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, assetUrl, thumbUrl, formatApiErrorDetail } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import PublicLayout from "@/components/PublicLayout";
import {
  Camera, MapPin, SpinnerGap, Images, Star, UserPlus, UserMinus, PaperPlaneTilt,
  PencilSimple, SealCheck, EnvelopeSimple, EyeSlash, Wrench, Globe, Check,
  LinkSimple, QrCode, Copy, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function Stars({ value = 0, size = 16, onPick }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} weight={i <= Math.round(value) ? "fill" : "regular"}
          onClick={onPick ? () => onPick(i) : undefined}
          data-testid={onPick ? `star-${i}` : undefined}
          className={`${i <= Math.round(value) ? "text-amber-500" : "text-zinc-300"} ${onPick ? "cursor-pointer transition-transform hover:scale-110" : ""}`} />
      ))}
    </span>
  );
}

export default function PhotographerPage() {
  const { key } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ bio: "", gear: "", website: "" });
  const [contact, setContact] = useState({ name: "", email: "", message: "", company: "" });
  const [contactSent, setContactSent] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 0, text: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(false);
    api.get(`/public/photographer/${encodeURIComponent(key)}`)
      .then((r) => {
        setData(r.data);
        setForm({ bio: r.data.bio || "", gear: (r.data.gear || []).join(", "), website: r.data.website || "" });
        if (r.data.my_review) setReviewForm({ rating: r.data.my_review.rating, text: r.data.my_review.text || "" });
      })
      .catch(() => setError(true));
  }, [key]);

  useEffect(() => { setData(null); load(); }, [load]);

  useSeo({
    title: data ? `${data.name} — PhotoPulse.pro` : "Photographer — PhotoPulse.pro",
    description: data ? `${data.count} public photos by ${data.name} on PhotoPulse.pro.` : "Photographer profile on PhotoPulse.pro",
  });

  const err = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

  const toggleFollow = async () => {
    if (!user) { toast.error("Sign in to follow photographers."); return; }
    try { await api.post(`/photographer/${encodeURIComponent(key)}/follow`); load(); }
    catch (e) { err(e); }
  };
  const claim = async () => {
    try { await api.post(`/photographer/${encodeURIComponent(key)}/claim`); toast.success("Profile claimed"); load(); }
    catch (e) { err(e); }
  };
  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.put(`/photographer/${encodeURIComponent(key)}`, {
        bio: form.bio, website: form.website,
        gear: form.gear.split(",").map((g) => g.trim()).filter(Boolean),
      });
      toast.success("Profile updated"); setEditing(false); load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const sendContact = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data: r } = await api.post(`/photographer/${encodeURIComponent(key)}/contact`, contact);
      toast.success(r.message || "Message sent"); setContactSent(true);
      setContact({ name: "", email: "", message: "", company: "" });
    } catch (e2) { err(e2); } finally { setBusy(false); }
  };
  const submitReview = async () => {
    if (!reviewForm.rating) { toast.error("Pick a star rating first."); return; }
    setBusy(true);
    try {
      await api.post(`/photographer/${encodeURIComponent(key)}/review`, { rating: reviewForm.rating, text: reviewForm.text });
      toast.success("Review submitted"); load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const hideReview = async (id, hidden) => {
    try { await api.post(`/photographer/${encodeURIComponent(key)}/review/${id}/hide`, { hidden }); load(); }
    catch (e) { err(e); }
  };
  const createShare = async () => {
    setBusy(true);
    try { await api.post(`/photographer/${encodeURIComponent(key)}/share`); toast.success("Share link + QR ready"); load(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  const copyShare = () => { navigator.clipboard.writeText(data.share.short_url); toast.success("Link copied"); };

  if (error) return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <p className="font-display text-2xl font-black">Photographer not found</p>
        <Link to="/explore" className="mt-6 inline-flex rounded-full bg-[#F27A54] px-5 py-2.5 text-sm font-bold text-white">Back to gallery</Link>
      </div>
    </PublicLayout>
  );
  if (!data) return <PublicLayout><div className="grid min-h-[60vh] place-items-center"><SpinnerGap size={36} className="animate-spin text-[#F27A54]" weight="bold" /></div></PublicLayout>;

  return (
    <PublicLayout>
      {/* ---------- Header ---------- */}
      <div className="border-b border-border bg-white">
        <div className="mx-auto max-w-6xl px-5 py-8">
          <div className="flex flex-wrap items-start gap-4">
            {data.avatar_url
              ? <img src={data.avatar_url} alt="" className="h-16 w-16 rounded-full object-cover ring-2 ring-[#F27A54]/20" />
              : <span className="grid h-16 w-16 place-items-center rounded-full bg-[#F27A54]/10 text-[#F27A54]"><Camera size={28} weight="fill" /></span>}
            <div className="min-w-0 flex-1">
              <p className="overline text-[#F27A54]">Photographer</p>
              <h1 data-testid="photographer-name" className="flex items-center gap-2 font-display text-3xl font-black tracking-tight">
                {data.name}
                {data.claimed && <SealCheck size={20} weight="fill" className="text-[#2D6A4F]" title="Claimed profile" />}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><Images size={14} weight="fill" /> {data.count} photo{data.count !== 1 ? "s" : ""}</span>
                <span className="flex items-center gap-1" data-testid="follower-count"><UserPlus size={14} weight="fill" /> {data.follower_count} follower{data.follower_count !== 1 ? "s" : ""}</span>
                {data.rating?.count > 0 && (
                  <span className="flex items-center gap-1" data-testid="rating-summary"><Stars value={data.rating.avg} size={14} /> {data.rating.avg} ({data.rating.count})</span>
                )}
                {data.views?.total > 0 && (
                  <span className="flex items-center gap-1" data-testid="views-badge"><Eye size={14} weight="fill" /> Viewed {data.views.total.toLocaleString()} times{data.views.top_label ? ` · mostly from ${data.views.top_label}` : ""}</span>
                )}
                {data.website && <a href={data.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-semibold text-[#F27A54] hover:underline"><Globe size={14} weight="fill" /> Website</a>}
              </div>
            </div>
            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2">
              {data.can_edit ? (
                <button onClick={() => setEditing((v) => !v)} data-testid="edit-profile-btn"
                  className="flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-bold text-zinc-700 hover:border-[#F27A54] hover:text-[#F27A54]">
                  <PencilSimple size={15} weight="bold" /> Edit profile
                </button>
              ) : user ? (
                <button onClick={toggleFollow} data-testid="follow-btn"
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${data.is_following ? "border border-border bg-white text-zinc-700 hover:border-red-300 hover:text-red-500" : "bg-[#F27A54] text-white hover:bg-[#d9633f]"}`}>
                  {data.is_following ? <><UserMinus size={15} weight="bold" /> Following</> : <><UserPlus size={15} weight="bold" /> Follow</>}
                </button>
              ) : (
                <Link to="/login" className="flex items-center gap-1.5 rounded-full bg-[#F27A54] px-4 py-2 text-sm font-bold text-white hover:bg-[#d9633f]"><UserPlus size={15} weight="bold" /> Follow</Link>
              )}
              {user && !data.claimed && !data.can_edit && (
                <button onClick={claim} data-testid="claim-btn"
                  className="flex items-center gap-1.5 rounded-full border border-[#2D6A4F] px-4 py-2 text-sm font-bold text-[#2D6A4F] hover:bg-[#2D6A4F]/5">
                  <SealCheck size={15} weight="bold" /> Claim this profile
                </button>
              )}
            </div>
          </div>

          {/* Bio + gear */}
          {editing ? (
            <div className="mt-5 rounded-2xl border border-border bg-[#FAFAF9] p-4" data-testid="edit-profile-form">
              <label className="overline text-[10px] text-zinc-500">Bio</label>
              <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} data-testid="edit-bio"
                className="mt-1 w-full rounded-md border border-border bg-white p-2.5 text-sm outline-none focus:border-[#F27A54]" placeholder="Tell people about your work…" />
              <label className="overline mt-3 block text-[10px] text-zinc-500">Gear (comma-separated)</label>
              <input value={form.gear} onChange={(e) => setForm({ ...form, gear: e.target.value })} data-testid="edit-gear"
                className="mt-1 w-full rounded-md border border-border bg-white p-2.5 text-sm outline-none focus:border-[#F27A54]" placeholder="Sony A7 IV, 24-70mm f/2.8, DJI Mini 4" />
              <label className="overline mt-3 block text-[10px] text-zinc-500">Website</label>
              <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="edit-website"
                className="mt-1 w-full rounded-md border border-border bg-white p-2.5 text-sm outline-none focus:border-[#F27A54]" placeholder="https://…" />
              <div className="mt-3 flex gap-2">
                <button onClick={saveProfile} disabled={busy} data-testid="save-profile-btn" className="flex items-center gap-1.5 rounded-full bg-[#F27A54] px-5 py-2 text-sm font-bold text-white hover:bg-[#d9633f] disabled:opacity-60">{busy ? <SpinnerGap size={15} className="animate-spin" /> : <Check size={15} weight="bold" />} Save</button>
                <button onClick={() => setEditing(false)} className="rounded-full border border-border px-5 py-2 text-sm font-bold text-zinc-600">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              {data.bio && <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-700" data-testid="profile-bio">{data.bio}</p>}
              {data.gear?.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="profile-gear">
                  <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-zinc-400"><Wrench size={13} weight="fill" /> Gear</span>
                  {data.gear.map((g) => <span key={g} className="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-semibold text-zinc-600">{g}</span>)}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-8 lg:grid-cols-[1fr_320px]">
        {/* ---------- Photos ---------- */}
        <div>
          <h2 className="mb-4 font-display text-lg font-black">Photos</h2>
          {data.photos.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border py-14 text-center text-sm text-muted-foreground">No public photos yet.</p>
          ) : (
            <div className="columns-2 gap-4 sm:columns-3 [&>*]:mb-4" data-testid="photographer-grid">
              {data.photos.map((p) => (
                <Link key={p.id} to={p.photo_id ? `/photo/${p.photo_id}` : `/p/${p.id}`} data-testid={`photographer-photo-${p.id}`}
                  className="group relative block break-inside-avoid overflow-hidden rounded-lg bg-zinc-100 shadow-sm ring-1 ring-black/5">
                  <img src={thumbUrl(p.image_url)} alt={p.title} loading="lazy"
                    className="w-full transition-transform duration-500 group-hover:scale-[1.04]" />
                  <div className="absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-black/75 to-transparent p-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                    <p className="line-clamp-1 text-sm font-bold text-white">{p.title || "Untitled"}</p>
                    {p.location?.city && <p className="flex items-center gap-1 text-xs text-white/80"><MapPin size={11} weight="fill" />{p.location.city}</p>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* ---------- Sidebar: contact + reviews ---------- */}
        <aside className="space-y-6">
          {/* Share & QR */}
          {(data.share || data.can_edit) && (
            <div className="rounded-2xl border border-border bg-white p-5" data-testid="share-card">
              <h3 className="flex items-center gap-2 font-display font-black"><LinkSimple size={18} weight="bold" className="text-[#F27A54]" /> Share & QR</h3>
              {data.share ? (
                <>
                  <div className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-[#FAFAF9] px-3 py-2">
                    <a href={data.share.short_url} target="_blank" rel="noopener noreferrer" data-testid="share-short-url"
                      className="min-w-0 flex-1 truncate text-sm font-bold text-[#F27A54] hover:underline">{data.share.short_url}</a>
                    <button onClick={copyShare} data-testid="share-copy" className="shrink-0 text-zinc-400 hover:text-[#F27A54]"><Copy size={16} weight="bold" /></button>
                  </div>
                  <img src={data.share.qr_svg} alt="Profile QR code" data-testid="share-qr"
                    className="mt-3 h-40 w-40 rounded-lg border border-border bg-white p-2" />
                  <p className="mt-2 text-xs text-muted-foreground">Print-ready SVG QR — crisp on cards, framed prints, exhibition plaques.</p>
                </>
              ) : (
                <>
                  <p className="mt-2 text-sm text-muted-foreground">Create a branded <b>splink.pro</b> short link + QR for this profile.</p>
                  <button onClick={createShare} disabled={busy} data-testid="create-share-btn"
                    className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-[#111] py-2.5 text-sm font-bold text-white hover:bg-black disabled:opacity-60">
                    {busy ? <SpinnerGap size={15} className="animate-spin" /> : <QrCode size={15} weight="bold" />} Create share link + QR
                  </button>
                </>
              )}
            </div>
          )}

          {/* Contact */}
          <div className="rounded-2xl border border-border bg-white p-5" data-testid="contact-card">
            <h3 className="flex items-center gap-2 font-display font-black"><EnvelopeSimple size={18} weight="bold" className="text-[#F27A54]" /> Contact {data.name}</h3>
            {contactSent ? (
              <p className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-800" data-testid="contact-sent">Message sent — thank you!</p>
            ) : (
              <form onSubmit={sendContact} className="mt-3 space-y-2">
                <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Your name" data-testid="contact-name"
                  className="w-full rounded-md border border-border p-2.5 text-sm outline-none focus:border-[#F27A54]" />
                <input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Your email" type="email" data-testid="contact-email"
                  className="w-full rounded-md border border-border p-2.5 text-sm outline-none focus:border-[#F27A54]" />
                <textarea value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} required rows={3} placeholder="Your message…" data-testid="contact-message"
                  className="w-full rounded-md border border-border p-2.5 text-sm outline-none focus:border-[#F27A54]" />
                <input tabIndex={-1} autoComplete="off" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })}
                  className="hidden" aria-hidden="true" />
                <button type="submit" disabled={busy} data-testid="contact-submit"
                  className="flex w-full items-center justify-center gap-1.5 rounded-full bg-[#2D6A4F] py-2.5 text-sm font-bold text-white hover:bg-[#245840] disabled:opacity-60">
                  {busy ? <SpinnerGap size={15} className="animate-spin" /> : <PaperPlaneTilt size={15} weight="fill" />} Send message
                </button>
              </form>
            )}
          </div>

          {/* Reviews */}
          <div className="rounded-2xl border border-border bg-white p-5" data-testid="reviews-card">
            <h3 className="flex items-center gap-2 font-display font-black"><Star size={18} weight="fill" className="text-amber-500" /> Reviews</h3>
            {data.rating?.count > 0 ? (
              <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground"><Stars value={data.rating.avg} size={15} /> <b className="text-zinc-800">{data.rating.avg}</b> · {data.rating.count} review{data.rating.count !== 1 ? "s" : ""}</p>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">No reviews yet.</p>
            )}

            {data.can_review && (
              <div className="mt-4 rounded-xl border border-border bg-[#FAFAF9] p-3" data-testid="review-form">
                <p className="text-xs font-bold uppercase tracking-wide text-zinc-500">{data.my_review ? "Update your review" : "Leave a review"}</p>
                <div className="mt-1.5"><Stars value={reviewForm.rating} size={22} onPick={(r) => setReviewForm({ ...reviewForm, rating: r })} /></div>
                <textarea value={reviewForm.text} onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })} rows={2} placeholder="Share your experience (optional)" data-testid="review-text"
                  className="mt-2 w-full rounded-md border border-border bg-white p-2.5 text-sm outline-none focus:border-[#F27A54]" />
                <button onClick={submitReview} disabled={busy} data-testid="review-submit"
                  className="mt-2 w-full rounded-full bg-[#F27A54] py-2 text-sm font-bold text-white hover:bg-[#d9633f] disabled:opacity-60">{data.my_review ? "Update review" : "Submit review"}</button>
              </div>
            )}
            {!user && <Link to="/login" className="mt-3 inline-block text-sm font-semibold text-[#F27A54] hover:underline">Sign in to leave a review</Link>}

            <div className="mt-4 space-y-3" data-testid="review-list">
              {data.reviews.map((rv) => (
                <div key={rv.id} className="border-t border-border/60 pt-3" data-testid={`review-${rv.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-bold text-zinc-800">{rv.reviewer_name}</p>
                    <Stars value={rv.rating} size={13} />
                  </div>
                  {rv.text && <p className="mt-1 text-sm leading-snug text-zinc-600">{rv.text}</p>}
                  {data.can_edit && (
                    <button onClick={() => hideReview(rv.id, true)} data-testid={`hide-review-${rv.id}`}
                      className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-zinc-400 hover:text-red-500"><EyeSlash size={12} weight="bold" /> Hide</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </PublicLayout>
  );
}
